'use client';
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { lockBodyScroll } from '@/utils/scrollLock';

const ModalContext = createContext(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
};

export const ModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalMaxWidthClass, setModalMaxWidthClass] = useState(null);
  const [modalMaxHeightClass, setModalMaxHeightClass] = useState(null);
  const [modalBackgroundColor, setModalBackgroundColor] = useState(null);
  const [modalAriaLabel, setModalAriaLabel] = useState('Modal dialog');
  const [modalBare, setModalBare] = useState(false);
  const [modalNoPadding, setModalNoPadding] = useState(false);
  const [modalFitContent, setModalFitContent] = useState(false);
  const [hideDefaultCloseButton, setHideDefaultCloseButton] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseHookRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const openModal = (content, maxWidth = null, backgroundColor = 'bg-warm-white', options = null, maxHeight = null) => {
    clearTimeout(closeTimeoutRef.current);
    setIsAnimating(false);
    setModalMaxWidthClass(maxWidth || 'max-w-6xl');
    setModalMaxHeightClass(maxHeight || 'max-h-full');
    setModalBackgroundColor(backgroundColor);
    const ariaLabel = options?.ariaLabel?.trim?.() || null;
    setModalAriaLabel(ariaLabel || 'Modal dialog');
    setModalBare(options?.bare === true);
    setModalNoPadding(options?.noPadding === true);
    setModalFitContent(options?.fitContent === true);
    setHideDefaultCloseButton(options?.hideDefaultCloseButton === true);
    setModalContent(content);
    setIsOpen(true);

    closeTimeoutRef.current = setTimeout(() => {
      setIsAnimating(true);
      closeTimeoutRef.current = null;
    }, 10);
  };

  const closeModal = () => {
    clearTimeout(closeTimeoutRef.current);
    setIsAnimating(false);
    // Wait for animation to complete before unmounting
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setModalContent(null);
      setModalAriaLabel('Modal dialog');
      setModalBare(false);
      setModalNoPadding(false);
      setModalFitContent(false);
      setHideDefaultCloseButton(false);
      if (typeof onCloseHookRef.current === 'function') {
        onCloseHookRef.current();
        onCloseHookRef.current = null;
      }
      closeTimeoutRef.current = null;
    }, 200); // Match this with the transition duration
  };

  const setAfterCloseHook = (callback) => {
    onCloseHookRef.current = callback;
  };

  // Helper function to get all focusable elements within the modal
  const getFocusableElements = () => {
    if (!modalRef.current) return [];

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ];

    return Array.from(
      modalRef.current.querySelectorAll(focusableSelectors.join(',')),
    );
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    return lockBodyScroll();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;

      // Defer focusing until after the modal content has been rendered and painted.
      const animationFrameId = window.requestAnimationFrame(() => {
        // Prefer focusing an actual interactive control (close button) over the dialog container.
        // This ensures keyboard users land on a meaningful element inside the dialog.
        const fallbackTarget =
          closeButtonRef.current ||
          getFocusableElements()[0] ||
          modalRef.current;
        fallbackTarget?.focus?.();
      });

      return () => {
        window.cancelAnimationFrame(animationFrameId);
      };
    } else {
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current?.focus?.();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        closeModal();

        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus?.();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus?.();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeModal();
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, setAfterCloseHook }}>
      <div aria-hidden={isOpen ? 'true' : 'false'}>
        {children}
      </div>
      {isOpen && (
        <div
          className={`fixed inset-0 z-50 flex w-full items-center justify-center transition-opacity duration-200 ${
            modalBare ? 'bg-warm-white' : 'bg-black/50 p-4 lg:p-0'
          } ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleBackdropClick}
        >
          <div
            ref={modalRef}
            className={`relative ${modalMaxHeightClass} ${modalFitContent ? 'w-fit' : 'w-full'} ${modalMaxWidthClass} ${modalBackgroundColor} overflow-y-auto ${modalBare || modalNoPadding ? '' : 'p-6'} transition-all duration-200 ${
              isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}
            onClick={(e) => e.stopPropagation()} // Prevent click bubbling
            role="dialog"
            aria-modal="true"
            aria-label={modalAriaLabel}
            tabIndex={-1}
          >
            {/* Modals that render their own close button (currently just the
                gallery lightbox) opt out of this default one explicitly,
                independent of `bare`'s background/padding behavior. */}
            {!hideDefaultCloseButton && (
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close modal"
                ref={closeButtonRef}
                className="font-worksans absolute grid place-items-center top-6 right-6 cursor-pointer text-primary w-8 h-8 text-3xl"
              >
                ×
              </button>
            )}
            {modalContent}
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
};
